import { createContext, useContext, useRef, useCallback, ReactNode } from "react";

/**
 * App-wide "is something unsaved right now?" registry.
 *
 * Problem: client-side navigation (sidebar `Link`s, logout) never fires
 * `beforeunload`, so a dirty editor (e.g. PageDetails' staged content-block
 * drafts) can be silently discarded by anything other than a real tab
 * close/reload or that page's own in-page "Back" button.
 *
 * Fix: any screen that stages unsaved work registers its dirty flag here via
 * `setDirty`. Anything that can navigate the user away outside of React
 * Router's normal flow (sidebar, logout button, ...) calls `isDirty()`
 * first and, if true, shows a confirmation before proceeding.
 *
 * Deliberately generic (not "PageDetails' dirty flag") so future screens can
 * register too, but kept to a single scalar — there is only one registrant
 * today and a per-screen id/Map would be over-engineering for that.
 *
 * Implemented with a ref (not state): reads happen imperatively at
 * click-time, so registering/updating dirty state never re-renders
 * consumers like the sidebar.
 */
interface UnsavedChangesContextType {
  /** Called by the dirty screen whenever its dirty state changes, and
   * (via effect cleanup) with `false` on unmount — see the doc on
   * `PageDetails`' registration effect for why that guarantees this can
   * never be left stuck `true` after the screen is gone. */
  setDirty: (dirty: boolean) => void;
  /** Called by navigation triggers to decide whether to warn before
   * leaving. */
  isDirty: () => boolean;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | undefined>(
  undefined
);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const dirtyRef = useRef(false);

  const setDirty = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  const isDirty = useCallback(() => dirtyRef.current, []);

  return (
    <UnsavedChangesContext.Provider value={{ setDirty, isDirty }}>
      {children}
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges() {
  const context = useContext(UnsavedChangesContext);
  if (context === undefined) {
    throw new Error(
      "useUnsavedChanges must be used within an UnsavedChangesProvider"
    );
  }
  return context;
}
